/* ═══════════════════════════════════════════════════════════════════════════════
   DR RECOGNITION PRIMITIVES — shared forward-cohort + renewal-lookback helpers for
   the deferred-revenue engines (AE Quota, Company Quota).

   Pure functions, no DB / no side-effects beyond the gaapRevenue accumulator the
   caller passes in (mirrors the existingBookRecognition.js / prepaidRouting.js
   extraction pattern). Collapses recognition idioms that were hand-copied across the
   DR engines into ONE place, so a future change lands once instead of N times.

   Scope note: existingBookRecognition.js owns the EXISTING-book primitives
   (renewing-flat / decaying run-off). This module owns the FORWARD-looking primitives
   (new-ACV / renewal / churn cohorts). Kept separate so each file's responsibility
   stays crisp.

   recognizeForwardCohort — a single annual cohort recognized acv/12 over a FIXED
   12-month term from saleMonth, last-month remainder = r2(acv − cumulative) to conserve
   the exact total. The `sign` param flips it between recognition (+1) and churn-of-book
   (−1). The helper writes into the passed `gaapRevenue` accumulator (guarded by the
   existing `!== undefined` keyspace check) and RETURNS the cohort's recognized/remaining
   maps — it deliberately does NOT push to any schedule. Schedule membership stays a
   CALLER decision (some sites push a display cohort, some don't), which is exactly what
   keeps the engines' `schedule` output byte-identical after extraction.
   ═══════════════════════════════════════════════════════════════════════════════ */

import { addMonths } from './dateUtils';

const RECOGNITION_TERM = 12; // annual recognition period — fixed, never window length

const r2 = n => Math.round(n * 100) / 100;

/**
 * Recognize one annual cohort over a fixed 12-month term from saleMonth.
 * @param {number} acv          — amount to recognize over the term
 * @param {string} saleMonth    — cohort start month "YYYY-MM"
 * @param {Object} gaapRevenue  — accumulator mutated in place: gaap[recMonth] += sign*recAmt
 *                                 (only for keys already present — preserves the engines'
 *                                  `!== undefined` keyspace guard)
 * @param {Object} [opts]
 * @param {number} [opts.sign=1] — +1 recognize, −1 churn-of-book
 * @returns {{ monthlyRecognition:number, recognized:Object, remaining:Object }}
 */
export function recognizeForwardCohort(acv, saleMonth, gaapRevenue, { sign = 1 } = {}) {
  const monthlyRec = r2(acv / 12);
  const recognized = {}, remaining = {};
  let cum = 0;
  for (let i = 0; i < RECOGNITION_TERM; i++) {
    const recMonth = addMonths(saleMonth, i);
    const recAmt = i < RECOGNITION_TERM - 1 ? monthlyRec : r2(acv - cum); // last month = remainder
    cum = r2(cum + recAmt);
    recognized[recMonth] = recAmt;
    remaining[recMonth] = r2(acv - cum);
    if (gaapRevenue[recMonth] !== undefined) {
      gaapRevenue[recMonth] = r2(gaapRevenue[recMonth] + sign * recAmt);
    }
  }
  return { monthlyRecognition: monthlyRec, recognized, remaining };
}

/**
 * Renewal lookback for one month — the shared Step-2 idiom (manual vs auto [m-12]
 * Total-ACV lookback). Returns the month's gross/net renewals, churn, and totalACV;
 * the CALLER assigns these back into its own arrays. In particular the caller MUST write
 * the returned `totalACV` into its totalACV[month] map AFTER calling, because a later
 * iteration's auto lookback reads totalACV[month-12] from that same in-progress map
 * (the function reads the passed `totalACV` reference for the [m-12] forecast-path read).
 *
 * @param {Object} a
 * @param {string} a.month            — "YYYY-MM"
 * @param {string} a.renewalMode      — 'manual' | 'auto'
 * @param {Object} a.manualRenewals   — { [month]: amount }
 * @param {Object} a.existingTotalACV — actuals-path lookback source (existing mode)
 * @param {Object} a.totalACV         — in-progress forecast totalACV map (read by ref)
 * @param {number} a.churnRate
 * @param {Object} a.newACV           — { [month]: amount } (pre-initialized to 0)
 * @param {string} a.closeMonth
 * @param {string} a.startMode        — 'zero' | 'existing'
 * @param {Object} [a.forecastSold]   -- the caller's in-progress FORECAST-SOLD annual value by month (billing terms P2, Q-B2):
 *                                      the caller writes the returned forecastSoldValue into it, as it does totalACV
 * @param {number} [a.adjustment]     -- a renewal adjustment in annual contract value, added to the gross renewal before churn
 *                                      (billing terms (n), Q-B1). Absent / 0 = none.
 * @returns {{ grossRenewals:number, churn:number, netRenewals:number, totalACV:number, forecastSoldRenewals:number,
 *             forecastSoldValue:number }}
 *
 * Q-B2 (Brian, 2026-10-26; billing terms P2): MANUAL renewal mode = the operator's TYPED renewals of the EXISTING book only.
 * Contracts SOLD IN THE FORECAST still auto-renew: the manual gross is typed[month] + the forecast-sold annual value booked
 * 12 months earlier (forecastSold[month - 12], only when that month is itself a forecast month). Churn applies to the whole
 * gross, as in auto. It used to be typed[month] ALONE, so a contract sold in the forecast never renewed (investigation F5).
 * ONE rule for AE and CQ: both callers keep the forecastSold track, so CQ's own Step 2 and the AE engine it delegates to agree.
 * Inert on a window of 12 forecast months or fewer (month - 12 is then a closed month and the track adds 0), and no company is
 * in manual mode (measured 2026-10-26). A caller that passes no forecastSold gets exactly the old expression.
 */
export function computeRenewalLookback({ month, renewalMode, manualRenewals, existingTotalACV, totalACV, churnRate, newACV, closeMonth, startMode, forecastSold, adjustment }) {
  const lookback = addMonths(month, -12);
  const forecastSoldRenewals = (forecastSold && lookback > closeMonth && forecastSold[lookback] !== undefined) ? r2(forecastSold[lookback]) : 0;
  let grossRenewals;
  if (renewalMode === 'manual') {
    grossRenewals = r2(r2(parseFloat((manualRenewals || {})[month]) || 0) + forecastSoldRenewals);
  } else {
    if (lookback <= closeMonth && startMode === 'existing') {
      grossRenewals = r2(parseFloat(existingTotalACV[lookback]) || 0);
    } else if (totalACV[lookback] !== undefined) {
      grossRenewals = r2(totalACV[lookback]);
    } else {
      grossRenewals = 0;
    }
  }
  if (adjustment) grossRenewals = r2(grossRenewals + adjustment);
  const churn = r2(grossRenewals * churnRate);
  const netRenewals = r2(grossRenewals - churn);
  const totalACVm = r2((newACV[month] || 0) + netRenewals);
  // the forecast-sold track: this month's new contracts + the forecast-sold renewals net of their own churn
  const forecastSoldValue = r2((newACV[month] || 0) + r2(forecastSoldRenewals - r2(forecastSoldRenewals * churnRate)));
  return { grossRenewals, churn, netRenewals, totalACV: totalACVm, forecastSoldRenewals, forecastSoldValue };
}

export { r2 };
